import { useSyncExternalStore } from 'react';

/**
 * Per-brand Features, mirrored from the super-admin panel's Features section.
 *
 * These are NOT the build-time `brand.features` in `types.ts` — that one is a
 * compiled-in vocabulary (aggregator / liveCasino / slots / …) describing what
 * a *build* contains. This one is the super admin's runtime, per-brand grant:
 * `GET /brand` returns it as `features`, the backend enforces the same keys via
 * `FeatureGuard` / `BrandFeaturesService`, and the ids MUST stay in step with
 * the backend's `brand-features.ts` and the panel's `lib/features.ts` — one
 * vocabulary living in three repos.
 *
 * Unlike Menu Visibility this is not chrome: a feature that is off also has its
 * routes refused (`FeatureGate`), its data withheld by the server, and its API
 * calls rejected. Menus hide a link; Features remove the product.
 */

export const BRAND_FEATURE_IDS = [
  // Product surfaces
  'sports', 'casino', 'fantasy', 'lottery', 'fd',
  // Account lifecycle
  'registration',
  // Cashier
  'deposit', 'withdrawal',
  // Payment rails
  'upi', 'bank', 'crypto', 'sgpay24', 'divinepay',
] as const;

export type BrandFeature = (typeof BRAND_FEATURE_IDS)[number];
export type BrandFeatures = Record<BrandFeature, boolean>;

/**
 * Resting state — everything ON.
 *
 * Deliberately NOT the backend's `DEFAULT_BRAND_FEATURES` (which has fantasy,
 * lottery, fd and the newer rails off). Those defaults are what a brand is
 * *created* with and they are then stored on the row; from that point the row
 * is authoritative and the server sends it here. What this map has to model is
 * the guard's rule instead — a feature is BLOCKED only when the brand
 * explicitly says `false` — so a brand predating a key, an unresolved host and
 * a failed `/brand` fetch all behave the way the API does rather than blanking
 * out products the operator never switched off.
 */
export const DEFAULT_BRAND_FEATURES: BrandFeatures = BRAND_FEATURE_IDS.reduce(
  (acc, id) => { acc[id] = true; return acc; },
  {} as BrandFeatures,
);

/* --- store ---------------------------------------------------------------- */
// Same shape as the Site Settings / Menu Visibility stores next door:
// useSyncExternalStore compares snapshots by identity, so the object is rebuilt
// only when something actually changes.
let snapshot: BrandFeatures = DEFAULT_BRAND_FEATURES;
const subs = new Set<() => void>();

function subscribe(cb: () => void) {
  subs.add(cb);
  return () => { subs.delete(cb); };
}

/** Merge whatever the server sent over the defaults; ignore junk keys. */
export function setBrandFeatures(raw: unknown): void {
  const next = { ...DEFAULT_BRAND_FEATURES };
  if (raw && typeof raw === 'object') {
    const src = raw as Record<string, unknown>;
    BRAND_FEATURE_IDS.forEach((id) => {
      if (typeof src[id] === 'boolean') next[id] = src[id] as boolean;
    });
  }
  snapshot = next;
  subs.forEach((f) => f());
}

/* --- hooks ---------------------------------------------------------------- */
export function useBrandFeatures(): BrandFeatures {
  return useSyncExternalStore(subscribe, () => snapshot);
}

/** Non-reactive read, for code outside the React tree (RTK, route helpers). */
export function getBrandFeatures(): BrandFeatures {
  return snapshot;
}

/**
 * Filter a list by its feature ids, against a map you already hold.
 *
 * Takes the map rather than reading the store so it stays honest inside render:
 * pass what `useBrandFeatures()` returned and the result re-computes when the
 * brand resolves. An entry with no feature id predates the vocabulary and is
 * always kept — removing something an operator has no toggle for would be a bug
 * they could not undo from the panel.
 */
export function filterByFeature<T>(
  items: readonly T[],
  features: BrandFeatures,
  getId: (item: T) => BrandFeature | undefined,
): T[] {
  return items.filter((item) => {
    const id = getId(item);
    return id === undefined || features[id] !== false;
  });
}
